/**
 * livecommerceCleverTap.ts
 *
 * Single-file implementation for Live Commerce and StoryBlock CleverTap
 * payload builders and enrichers. This file consolidates all CT-related
 * helpers so consumers import from one stable module. It intentionally
 * keeps function signatures explicit and JSDoc-documented for clarity
 * and easier unit testing.
 */

import { componentNameConstants, dlElementConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  influencerPageClickTypes,
  influencerPageComponents,
  influencerPageEventNames,
  influencerPageEventTypes,
  influencerPageJourneyNames,
  influencerPageNames,
  influencerPagePropertyTypes,
  influencerPageReferenceIds,
} from "../constants/influencer-page.constants";
import {
  livelandingPageClickTypes,
  livelandingPageComponents,
  livelandingPageEventNames,
  livelandingPageEventTypes,
  livelandingPageJourneyNames,
  livelandingPagePropertyTypes,
  livelandingPageReferenceIds,
} from "../constants/livelanding-page.constants";
import { getSubsectionFromHashtags } from "./mediaUtils";
import { getCtSectionTitle } from "../components/HeadlessPlaylistSection/headlessPlaylistUtils";

const unifiedMediaCardVideoDescriptionMap: Record<string, string> = {};

type FireworkProductClickDetail = {
  video?: {
    encoded_id?: string;
    description?: string;
  };
  name?: string;
  product?: {
    title?: string;
    product_name?: string;
    id?: string;
    custom_cta_url?: string;
    url?: string;
  };
  product_id?: string;
};

/**
 * Track Firework product-click analytics for UnifiedMediaCard videos.
 * Returns false when event does not belong to the current card.
 */
export function trackUnifiedMediaCardProductClick({
  event,
  encodedId,
  fallbackVideoDescription,
}: {
  event: Event;
  encodedId?: string;
  fallbackVideoDescription?: string;
}): boolean {
  const detail = ((event as CustomEvent).detail || {}) as FireworkProductClickDetail;
  const clickedVideoId = detail?.video?.encoded_id || detail?.name?.replace("player-", "") || "";

  // Track only when event belongs to current card video.
  if (!encodedId || clickedVideoId !== encodedId) {
    return false;
  }

  const fwDescription = detail?.video?.description;
  if (clickedVideoId && fwDescription) {
    unifiedMediaCardVideoDescriptionMap[clickedVideoId] = fwDescription;
  }

  const productName =
    fwDescription ||
    unifiedMediaCardVideoDescriptionMap[clickedVideoId] ||
    fallbackVideoDescription ||
    "NA";

  const productTitle = detail?.product?.title || detail?.product?.product_name || "View Offers";
  const productId = detail?.product_id || detail?.product?.id || "NA";

  const cleverTapObject = {
    EVENT_NAME: "LIVE_COMMERCE_PAGE_CLICKED",
    EP_COMPONENT_NAME: "explore_top_products",
    EP_CLICK_TYPE: "video_card_button_click",
    EP_REFERENCE_ID: "explore_top_products",
    EP_PROPERTY_TYPE: "clp",

    EP_SECTION: "Explore Top Products",
    EP_CTA: "PRODUCT_CLICK",
    EP_CTA_TEXT: "PRODUCT_CLICK",

    EP_PAGE_NAME: "live-page",
    EP_EVENT_TYPE: "click",

    EP_JOURNEY: "LIVE_LANDING",
    EP_JOURNEY_NAME: "LIVE_LANDING",

    EP_PRODUCT_NAME: productName,
    EP_PRODUCT_TITLE: productTitle,
    EP_PRODUCT_ID: productId,

    EP_PRODUCT_CATEGORY: `${productName} || ${encodedId}`,

    EP_DESTINATION: detail?.product?.custom_cta_url || detail?.product?.url || "NA",
  };

  dataLayer("", {}, cleverTapObject);
  return true;
}

/**
 * Build analytics payload for Live Landing video card clicks.
 */
export function buildLiveLandingVideoCardClickCT({
  section,
  ctaText,
  videoId,
  videoTitle,
  liveSession,
  productName,
  productCategory,
  searchTerm,
  subSection,
}: {
  section: string;
  ctaText: string;
  videoId: string;
  videoTitle: string;
  liveSession: string;
  productName: string;
  productCategory: string;
  searchTerm: string;
  subSection: string;
}): Record<string, string> {
  return {
    EVENT_NAME: livelandingPageEventNames.LIVE_COMMERCE_PAGE_CLICKED,
    EP_COMPONENT_NAME: livelandingPageComponents.VIDEO_CARD,
    EP_CLICK_TYPE: livelandingPageClickTypes.VIDEO_CARD_BUTTON_CLICK,
    EP_REFERENCE_ID: livelandingPageReferenceIds.RECENT_LIVE_VIDEOS,
    EP_PROPERTY_TYPE: livelandingPagePropertyTypes.CLP,
    EP_SECTION: section,
    EP_CTA_TEXT: ctaText,
    EP_EVENT_TYPE: livelandingPageEventTypes.CLICK,
    EP_JOURNEY: "LIVE_COMMERCE",
    EP_JOURNEY_NAME: livelandingPageJourneyNames.LIVE_LANDING,
    EP_VIDEO_TITLE: videoTitle,
    EP_LIVEVIDEO_IMPRESSION: videoId,
    EP_ID: videoId,
    EP_LIVE_SESSION: liveSession,
    EP_POSITION: "1",
    EP_SR_NUMBER: "1",
    EP_SEGMENT: "LIVE_COMMERCE",
    EP_SPRINT: "DPAOP 3",
    EP_NUDGE: "NA",
    EP_PRODUCT_NAME: productName,
    EP_PRODUCT_CATEGORY: productCategory,
    EP_SEARCH: searchTerm,
    EP_SUB_SECTION: subSection,
  };
}

/**
 * Build analytics payload for reminder actions on Live Landing.
 */
export function buildLiveLandingReminderCT({
  videoId,
  videoTitle,
  cta,
  section,
  creatorName,
}: {
  videoId: string;
  videoTitle: string;
  cta: "SET_REMINDER" | "REMINDER_REMOVED";
  section: string;
  creatorName?: string;
}): Record<string, string> {
  return {
    EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
    EP_PAGE_NAME: dlElementConstants.LIVE_COMMERCE_CLP,
    EP_JOURNEY_NAME: dlElementConstants.LIVE_COMMERCE,
    EP_LIVEVIDEO_IMPRESSION: videoId,
    EP_ID: videoId,
    EP_VIDEO_TITLE: videoTitle,
    EP_LIVE_SESSION: "UPCOMING",
    EP_POSITION: "1",
    EP_SR_NUMBER: "1",
    EP_JOURNEY: "LIVE_COMMERCE",
    EP_SEGMENT: "LIVE_COMMERCE",
    EP_SPRINT: "DPAOP 3",
    EP_NUDGE: "NA",
    EP_CTA: cta,
    EP_SECTION: section,
    EP_CREATOR_NAME: creatorName || "",
  };
}

export function getLiveSessionState(status?: string): string {
  return status === "active" ? "ACTIVE" : "INACTIVE";
}

export type VideoDetailsVideo = {
  encoded_id?: string;
  live_stream_status?: string;
  caption?: string;
};

type VideoPlayerProductDetails = {
  product_name?: string;
  title?: string;
  product_id?: string;
  id?: string;
  custom_cta_url?: string;
  url?: string;
};

/**
 * Enrich base CT object with video/player specific fields used by StoryBlock
 * and Live Commerce components.
 */
export function enrichVideoPlayerCT(
  base: Record<string, string>,
  params: {
    video?: VideoDetailsVideo;
    ctaText: string;
    videoIdFallback: string;
    eventName?: string;
    sectionTitle?: string;
    creatorName?: string;
    sprint?: string;
  }
) {
  const {
    video,
    ctaText,
    videoIdFallback,
    eventName,
    sectionTitle,
    creatorName,
    sprint = "DPAOP",
  } = params;
  const result = {
    ...base,
    EP_LIVEVIDEO_IMPRESSION: video?.encoded_id || videoIdFallback || "NA",
    EP_ID: video?.encoded_id || videoIdFallback || "NA",
    EP_LIVE_SESSION: getLiveSessionState(video?.live_stream_status),
    EP_VIDEO_TITLE: video?.caption || eventName || "NA",
    EP_CTA: ctaText,
    EP_SECTION: sectionTitle || dlElementConstants.LIVE_SESSIONS,
    EP_CREATOR_NAME: creatorName || "NA",
    EP_POSITION: "1",
    EP_SR_NUMBER: "1",
    EP_JOURNEY: "LIVE_COMMERCE",
    EP_SEGMENT: "LIVE_COMMERCE",
    EP_SPRINT: sprint,
    EP_NUDGE: "NA",
    EP_LANG: "eng",
  };
  return result;
}

export type EnrichVideoPlayerParams = {
  video?: VideoDetailsVideo;
  ctaText: string;
  videoIdFallback: string;
  eventName?: string;
  sectionTitle?: string;
  creatorName?: string;
  sprint?: string;
};

export type EnrichVideoPlayerCT = (
  base: Record<string, string>,
  params: EnrichVideoPlayerParams
) => Record<string, string>;

/**
 * Build video player analytics payload pair: a lightweight `data` object
 * and the richer CleverTap object.
 */
export function buildVideoPlayerAnalyticsPayload({
  eventType,
  ctaText,
  sectionTitle,
  videoIdFallback,
  creatorName,
  eventName,
  video,
  eventNameKey = dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
  sprint = "DPAOP 3",
}: {
  eventType: string;
  ctaText: string;
  sectionTitle: string;
  videoIdFallback: string;
  creatorName?: string;
  eventName?: string;
  video?: VideoDetailsVideo;
  eventNameKey?: string;
  sprint?: string;
}): { data: Record<string, string>; cleverTapObject: Record<string, string> } {
  const data = {
    section_title: sectionTitle,
    component: componentNameConstants.UPCOMING_LIVE,
    event: eventType,
    element: eventType,
    cta_text: ctaText,
  };

  const cleverTapObject = enrichVideoPlayerCT(
    {
      EVENT_NAME: eventNameKey,
      EP_PRODUCT_NAME: "live-commerce-page",
      EP_PRODUCT_CATEGORY: "live-commerce",
      EP_EVENT_TYPE: "click",
      EP_PAGE_NAME: "live-commerce-page",
      EP_JOURNEY_NAME: dlElementConstants.LIVE_COMMERCE,
      EP_LANG: "eng",
    },
    {
      video,
      ctaText,
      videoIdFallback,
      eventName,
      sectionTitle: dlElementConstants.LIVE_SESSIONS,
      creatorName,
      sprint,
    }
  );

  return { data, cleverTapObject };
}

/**
 * Build analytics payload for VideoPlayer product card clicks.
 * Keeps product-click CT enrichment centralized in this file.
 */
export function buildVideoPlayerProductClickPayload({
  eventType,
  sectionTitle,
  videoIdFallback,
  creatorName,
  eventName,
  video,
  product,
}: {
  eventType: string;
  sectionTitle: string;
  videoIdFallback: string;
  creatorName?: string;
  eventName?: string;
  video?: VideoDetailsVideo;
  product?: VideoPlayerProductDetails;
}): { data: Record<string, string>; cleverTapObject: Record<string, string> } {
  const { data } = buildVideoPlayerAnalyticsPayload({
    eventType,
    ctaText: "PRODUCT_CLICK",
    sectionTitle,
    videoIdFallback,
    creatorName,
    eventName,
    video,
    eventNameKey: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
  });

  const resolvedVideoId = video?.encoded_id || videoIdFallback || "NA";
  const productTitle = product?.title || product?.product_name || "NA";
  const productId = product?.id || product?.product_id || "NA";
  const productName = video?.caption || eventName || "NA";

  return {
    data,
    cleverTapObject: {
      EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
      EP_COMPONENT_NAME: "explore_top_products",
      EP_CLICK_TYPE: "video_card_button_click",
      EP_REFERENCE_ID: "explore_top_products",
      EP_PROPERTY_TYPE: "clp",

      EP_SECTION: "Explore Top Products",
      EP_CTA: "PRODUCT_CLICK",
      EP_CTA_TEXT: "PRODUCT_CLICK",

      EP_PAGE_NAME: "live-page",
      EP_EVENT_TYPE: "click",

      EP_JOURNEY: "LIVE_LANDING",
      EP_JOURNEY_NAME: "LIVE_LANDING",

      EP_PRODUCT_NAME: productName,
      EP_PRODUCT_TITLE: productTitle,
      EP_PRODUCT_ID: productId,
      EP_PRODUCT_CATEGORY: `${productName} || ${resolvedVideoId}`,
      EP_DESTINATION: product?.custom_cta_url || product?.url || "NA",
    },
  };
}

/**
 * Build analytics for previous live thumbnails on influencer pages.
 */
export function buildPreviousLiveThumbnailAnalytics({
  previousLiveSessionHeading,
  eventName,
  videoId,
  categoryFromVideo,
  position,
  status,
  influencerName,
}: {
  previousLiveSessionHeading: string;
  eventName?: string;
  videoId: string;
  categoryFromVideo: string;
  position: number;
  status?: string;
  influencerName?: string;
}) {
  return {
    EVENT_NAME: influencerPageEventNames.SOCIAL_COMMERCE_CLICKED,
    EP_COMPONENT_NAME: influencerPageComponents.VIDEO_CARD,
    EP_CLICK_TYPE: influencerPageClickTypes.VIDEO_CARD_BUTTON_CLICK,
    EP_REFERENCE_ID: influencerPageReferenceIds.RECENT_LIVE_VIDEOS,
    EP_PROPERTY_TYPE: influencerPagePropertyTypes.CLP,
    EP_SECTION: previousLiveSessionHeading,
    EP_CTA_TEXT: eventName || "Explore Now",
    EP_PAGE_NAME: influencerPageNames.INFLUENCER_PAGE,
    EP_EVENT_TYPE: influencerPageEventTypes.CLICK,
    EP_JOURNEY: "INFLUENCER_PAGE",
    EP_JOURNEY_NAME: influencerPageJourneyNames.INFLUENCERS,
    EP_VIDEO_TITLE: eventName || "NA",
    EP_ID: videoId,
    EP_LIVEVIDEO_IMPRESSION: videoId,
    EP_LIVE_SESSION: status === "active" ? "ACTIVE" : "INACTIVE",
    EP_POSITION: (position > 0 ? position : 1).toString(),
    EP_SR_NUMBER: (position > 0 ? position : 1).toString(),
    EP_SEGMENT: "INFLUENCER_PAGE",
    EP_SPRINT: "DPAOP",
    EP_NUDGE: "NA",
    EP_PRODUCT_NAME: categoryFromVideo,
    EP_PRODUCT_CATEGORY: `${categoryFromVideo || "NA"} || ${videoId}`,
    EP_SOURCE: "influencer_page",
    EP_PAGE_LOAD: "FULLY LOADED",
    ...(influencerName && { EP_CREATOR_NAME: influencerName }),
  };
}

/**
 * Build analytics for previous live product clicks on influencer pages.
 */
export function buildPreviousLiveProductAnalytics({
  previousLiveSessionHeading,
  videoTitle,
  videoId,
  categoryFromVideo,
  position,
  status,
  productName,
  productId,
  unitUrl,
  influencerName,
}: {
  previousLiveSessionHeading: string;
  videoTitle: string;
  videoId: string;
  categoryFromVideo: string;
  position: number;
  status?: string;
  productName: string;
  productId: string;
  unitUrl: string;
  influencerName?: string;
}) {
  return {
    EVENT_NAME: influencerPageEventNames.SOCIAL_COMMERCE_CLICKED,
    EP_COMPONENT_NAME: influencerPageComponents.VIDEO_CARD,
    EP_CLICK_TYPE: influencerPageClickTypes.VIDEO_CARD_BUTTON_CLICK,
    EP_REFERENCE_ID: influencerPageReferenceIds.RECENT_LIVE_VIDEOS,
    EP_PROPERTY_TYPE: influencerPagePropertyTypes.CLP,
    EP_SECTION: previousLiveSessionHeading,
    EP_CTA_TEXT: "PRODUCT_CLICK",
    EP_CTA: "PRODUCT_CLICK",
    EP_PAGE_NAME: influencerPageNames.INFLUENCER_PAGE,
    EP_EVENT_TYPE: influencerPageEventTypes.CLICK,
    EP_JOURNEY: "INFLUENCER_PAGE",
    EP_JOURNEY_NAME: influencerPageJourneyNames.INFLUENCERS,
    EP_VIDEO_TITLE: videoTitle || "NA",
    EP_ID: videoId,
    EP_LIVEVIDEO_IMPRESSION: videoId,
    EP_LIVE_SESSION: status === "active" ? "ACTIVE" : "INACTIVE",
    EP_POSITION: (position > 0 ? position : 1).toString(),
    EP_SR_NUMBER: (position > 0 ? position : 1).toString(),
    EP_SEGMENT: "INFLUENCER_PAGE",
    EP_SPRINT: "DPAOP",
    EP_NUDGE: "NA",
    EP_PRODUCT_TITLE: productName,
    EP_PRODUCT_ID: productId,
    EP_PRODUCT_NAME: categoryFromVideo,
    EP_PRODUCT_CATEGORY: `${categoryFromVideo || "NA"} || ${videoId}`,
    EP_DESTINATION: unitUrl,
    EP_SOURCE: "influencer_page",
    EP_PAGE_LOAD: "FULLY LOADED",
    ...(influencerName && { EP_CREATOR_NAME: influencerName }),
  };
}

function resolveStoryBlockEventName(eventName: unknown, origin?: string): unknown {
  if (origin !== "influencers") return eventName;
  if (eventName === dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED) {
    return influencerPageEventNames.SOCIAL_COMMERCE_CLICKED;
  }
  if (eventName === dlElementConstants.LIVE_COMMERCE_PAGE_VIEWED) {
    return influencerPageEventNames.SOCIAL_COMMERCE_VIEWED;
  }
  return eventName;
}

function resolveStoryBlockSource(origin: string | undefined, eventName: unknown): string {
  if (origin === "influencers") return "influencer_page";
  if (eventName === dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED) {
    return "live-landing-page";
  }
  return "NA";
}

/**
 * Enrich StoryBlock-specific CleverTap object. Provides mapping from live
 * commerce event names to influencer equivalents when `origin` is
 * `influencers` and populates standard EP_* fields used by consumers and
 * tests.
 */
export function enrichStoryBlockCT(
  base: Record<string, any>,
  params: {
    origin?: string;
    videoDetails?: {
      video?: {
        encoded_id?: string;
        caption?: string;
        live_stream_status?: string;
        description?: string;
        event_description?: string;
        hashtags?: string[];
      };
    };
    customerId?: string;
    title?: string;
    titleCt?: string;
    eventType?: string;
    position?: number;
    eventTypeValue?: "click" | "view";
  }
) {
  const {
    origin,
    videoDetails,
    customerId,
    title,
    titleCt,
    position = 1,
    eventTypeValue = "click",
  } = params;

  const ctSectionTitle = getCtSectionTitle(titleCt, title);
  const videoEncodedId = videoDetails?.video?.encoded_id || "NA";

  const resolvedEventName = base.EVENT_NAME;
  const finalEventName = resolveStoryBlockEventName(resolvedEventName, origin);
  const resolvedEpSource = resolveStoryBlockSource(origin, resolvedEventName);
  const videoDescriptionMap =
    typeof window !== "undefined" ? window.__fwVideoDescriptionMap : undefined;
  const analyticsWindow =
    typeof window !== "undefined"
      ? (window as Window & { clevertap?: { ctPageObject?: Record<string, unknown> } })
      : undefined;

  if (resolvedEpSource !== "NA" && analyticsWindow?.clevertap?.ctPageObject) {
    analyticsWindow.clevertap.ctPageObject = {
      ...analyticsWindow.clevertap.ctPageObject,
      EP_SOURCE: resolvedEpSource,
    };
  }

  const videoDescription =
    videoDetails?.video?.description ||
    videoDetails?.video?.event_description ||
    (videoEncodedId !== "NA" ? videoDescriptionMap?.[videoEncodedId] : undefined) ||
    "NA";

  const result = {
    ...base,
    EVENT_NAME: finalEventName,

    EP_COMPONENT_NAME: "STORYBLOCK",
    ...(eventTypeValue === "click" ? { EP_CLICK_TYPE: "ICON_CLICK" } : {}),
    EP_REFERENCE_ID: "STORYBLOCK",
    EP_PROPERTY_TYPE: "clp",

    EP_SECTION: ctSectionTitle || videoDetails?.video?.caption || "NA",
    ...(eventTypeValue === "click"
      ? {
          EP_CTA_TEXT: base.EP_CTA || "PRODUCT_CLICK",
          EP_CTA: base.EP_CTA || "PRODUCT_CLICK",
        }
      : {}),

    EP_PRODUCT_NAME: videoDescription,

    EP_PRODUCT_CATEGORY: `${videoDescription} || ${videoEncodedId}`,

    EP_PAGE_NAME: origin === "influencers" ? "INFLUENCER_PAGE" : "LIVE LANDING PAGE CLP",

    EP_PAGE_URL: typeof window !== "undefined" ? window.location.href : "NA",
    EP_SOURCE: resolvedEpSource,

    EP_EVENT_TYPE: (eventTypeValue || "click").toUpperCase(),
    EP_VIDEO_TYPE: "VIDEO_POP_UP",
    EP_JOURNEY: origin === "influencers" ? "INFLUENCER_PAGE" : "LIVE_COMMERCE",
    EP_SEGMENT: origin === "influencers" ? "SOCIAL_COMMERCE" : "LIVE_COMMERCE",

    EP_ID: videoDetails?.video?.encoded_id || "NA",
    EP_LIVEVIDEO_IMPRESSION: videoDetails?.video?.encoded_id || "NA",
    EP_VIDEO_TITLE: videoDetails?.video?.caption || "NA",
    EP_STATUS: videoDetails?.video?.live_stream_status || "NA",
    EP_LIVE_SESSION: videoDetails?.video?.live_stream_status === "active" ? "ACTIVE" : "INACTIVE",

    EP_POSITION: (position || 1).toString(),
    EP_SR_NUMBER: (position || 1).toString(),
    EP_CUSTOMER_ID: customerId || "NA",
    EP_CUSTOMER_TYPE: "NTB",
    EP_SPRINT: "DPAOP3",

    EP_DESTINATION: "NA",
    EP_SUB_SECTION: getSubsectionFromHashtags(videoDetails?.video?.hashtags),
    EP_NUDGE: "NA",
    EP_NUDGE_VALUE: "NA",
    EP_SEARCH: "NA",
    EP_CAMPAIGN: "NA",
    EP_LANG: "eng",
  };

  return result;
}
